import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import lodash from "lodash";
import * as React from "react";

import { useDebouncedCallback } from "@/shared/hooks";
import {
  ThreeDPanelState,
  usePanelState,
  useVizDispatch,
} from "@/shared/state/visualization";
import { setThreeDPanelConfig } from "@/shared/state/visualization/actions";
import {
  DEFAULT_DATA_SETTINGS,
  type DataSettings as DataSettingsSpec,
} from "@/shared/state/visualization/schema/v1/three-d";

import { type NumberInputState, readEmptyValueEdits } from "./numberInputEdits";
import styles from "./SceneSettings.module.css";

/** How long typing must pause before a typed threshold is saved. */
export const THRESHOLD_SAVE_DEBOUNCE_MS = 150;

interface DataSettingsProps {
  panelId: string;
}

/**
 * The panel's data settings editor: how every entity reads its topic data, as
 * opposed to how any one of them is drawn, wired to the visualization store.
 *
 * An unset interpolation toggle is shown as `DEFAULT_DATA_SETTINGS` has it,
 * which is also what the renderer resolves it to. An unset threshold is shown
 * empty rather than as its default of 0 Hz: both blend every pair of samples,
 * and empty says the panel never chose one. An edit spreads the stored
 * settings under the one it changes: `setThreeDPanelConfig` merges the config
 * one key deep, so it replaces `dataSettings` whole.
 *
 * The threshold's text is seeded from the store once and then owned here, as
 * `GridFields` owns its fields' text, so a half-typed value is not rewritten
 * under the caret. Emptying the field clears the stored threshold back to the
 * default; a value the schema would refuse is reported and not saved, and so
 * is text the input can't parse yet, such as "5e" on its way to an exponent,
 * which a number input reports as "" just as it does an emptied field. The
 * field is left out while interpolation is off, when every pair of samples is
 * held whatever it says; its text survives, so turning interpolation back on
 * shows it as it was, except text it couldn't parse, which unmounting loses
 * and which comes back as the stored threshold.
 *
 * A typed threshold is saved once typing settles, or as soon as the field loses
 * focus, rather than at every keystroke: each threshold saved rebakes every
 * entity's global trajectory (see `syncDataSettings`).
 *
 * MUI controls, styled as the rest of the settings sidebar is (the checkbox as
 * `VectorFieldConfig`'s "Enable gradient", the field as `GridFields`'), until
 * the sidebar moves to the foundation primitives as a whole.
 */
export function DataSettings({ panelId }: DataSettingsProps) {
  const dispatch = useVizDispatch();
  const panelState = usePanelState(panelId) as ThreeDPanelState;
  const stored = panelState.config?.dataSettings;
  const interpolating =
    stored?.interpolationEnabled ?? DEFAULT_DATA_SETTINGS.interpolationEnabled;

  const [thresholdText, setThresholdText] = React.useState(() =>
    String(stored?.interpolationThresholdHz ?? ""),
  );
  const [thresholdBadInput, setThresholdBadInput] = React.useState(false);
  const threshold = thresholdText === "" ? undefined : Number(thresholdText);
  const thresholdValid =
    !thresholdBadInput &&
    (threshold === undefined || (Number.isFinite(threshold) && threshold >= 0));

  const save = React.useCallback(
    (dataSettings: DataSettingsSpec) => {
      dispatch(setThreeDPanelConfig(panelId, { dataSettings }));
    },
    [dispatch, panelId],
  );
  const saveThreshold = useDebouncedCallback(save, THRESHOLD_SAVE_DEBOUNCE_MS);

  const onInterpolationChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (thresholdBadInput) {
        setThresholdText(String(stored?.interpolationThresholdHz ?? ""));
        setThresholdBadInput(false);
      }
      // Drop a threshold save queued with the old `stored`, so it can't fire
      // after this one and undo it. Leaving the field for the checkbox has
      // already flushed it, so the threshold it carried is saved, not lost.
      saveThreshold.cancel();
      save({ ...stored, interpolationEnabled: event.target.checked });
    },
    [save, saveThreshold, stored, thresholdBadInput],
  );

  const readThreshold = React.useCallback(
    (input: NumberInputState) => {
      const text = input.value;
      // Kept as "" even for text the input can't parse: holding the last good
      // text instead would have React write it back over what is being typed.
      setThresholdText(text);
      setThresholdBadInput(input.validity.badInput);
      if (input.validity.badInput) {
        return;
      }
      if (text === "") {
        saveThreshold(lodash.omit(stored, "interpolationThresholdHz"));
        return;
      }
      const interpolationThresholdHz = Number(text);
      if (
        Number.isFinite(interpolationThresholdHz) &&
        interpolationThresholdHz >= 0
      ) {
        saveThreshold({ ...stored, interpolationThresholdHz });
      }
    },
    [saveThreshold, stored],
  );

  return (
    <div className={styles.container}>
      <FormControlLabel
        control={
          <Checkbox
            checked={interpolating}
            onChange={onInterpolationChange}
            size="small"
          />
        }
        label="Smooth motion with data interpolation"
        className={styles.input}
      />
      {interpolating && (
        <TextField
          label="Interpolation Minimum Frequency (Hz)"
          type="number"
          size="small"
          autoComplete="off"
          fullWidth
          value={thresholdText}
          onChange={(event) => readThreshold(event.target)}
          onBlur={() => saveThreshold.flush()}
          error={!thresholdValid}
          helperText={
            thresholdValid ? "" : "Enter a frequency of 0 Hz or more."
          }
          className={styles.field}
          InputLabelProps={{ className: styles.input }}
          InputProps={{ className: styles.input }}
          inputProps={{
            min: 0,
            step: "any",
            className: styles.input,
            onInput: readEmptyValueEdits(readThreshold),
          }}
        />
      )}
    </div>
  );
}
